import {
  ArrowUp,
  Check,
  FolderKanban,
  Home,
  ListTodo,
  MessagesSquare,
  Files,
  Users,
  Sparkles,
  HardDrive,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AuroraField } from "@/components/visuals/AuroraField";
import { useLocale } from "@/i18n";
import { cn } from "@/lib/cn";

/**
 * A representation of the THKY workspace.
 *
 * Built as real markup rather than a screenshot so it stays crisp, adapts to
 * RTL and LTR, and reflows on mobile. It is illustrative of the product's
 * shape — it is not connected to a backend and shows no real data.
 */
export function ProductPreview() {
  const { t, dir } = useLocale();

  const nav: Array<{ icon: LucideIcon; label: string; active: boolean }> = [
    { icon: Home, label: t.preview.nav.home, active: false },
    { icon: Sparkles, label: t.preview.nav.assistant, active: true },
    { icon: MessagesSquare, label: t.preview.nav.boardroom, active: false },
    { icon: FolderKanban, label: t.preview.nav.projects, active: false },
    { icon: Users, label: t.preview.nav.agents, active: false },
    { icon: Files, label: t.preview.nav.files, active: false },
    { icon: ListTodo, label: t.preview.nav.tasks, active: false },
  ];

  const steps = [
    t.preview.conversation.step1,
    t.preview.conversation.step2,
    t.preview.conversation.step3,
  ];

  return (
    <section className="relative pb-20 md:pb-28">
      <AuroraField variant="soft" />

      <div className="container-page relative">
        <p className="mb-5 text-center text-sm text-cream-100/45">
          {t.preview.label}
        </p>

        <div
          className={cn(
            "mx-auto max-w-5xl overflow-hidden rounded-2xl md:rounded-3xl",
            "border border-white/10 bg-ink-900",
            "shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]",
          )}
        >
          {/* Window chrome */}
          <div className="flex items-center gap-3 border-b border-white/8 bg-ink-950/60 px-4 py-3">
            <div className="flex gap-1.5" aria-hidden>
              <span className="h-2.5 w-2.5 rounded-full bg-white/12" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/12" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/12" />
            </div>
            <p className="flex-1 truncate text-center text-xs text-cream-100/40">
              {t.preview.workspace}
            </p>
            <div className="w-12" aria-hidden />
          </div>

          <div className="grid md:grid-cols-[13rem_1fr]">
            {/* Sidebar — hidden on the narrowest screens so the conversation leads */}
            <aside className="hidden border-white/8 p-3 md:block md:border-e">
              <ul className="space-y-0.5">
                {nav.map(({ icon: Icon, label, active }) => (
                  <li key={label}>
                    <span
                      className={cn(
                        "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[0.8rem]",
                        active
                          ? "bg-emerald-500/10 text-cream-50 ring-1 ring-emerald-400/20"
                          : "text-cream-100/45",
                      )}
                    >
                      <Icon size={15} aria-hidden />
                      <span className="truncate">{label}</span>
                    </span>
                  </li>
                ))}
              </ul>

              {/* Processing-mode indicator — always visible in the real product */}
              <div className="mt-4 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.07] p-3">
                <p className="text-[0.68rem] tracking-wide text-emerald-200/70">
                  {t.preview.modeLabel}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-cream-50">
                  <HardDrive size={14} className="text-emerald-300" aria-hidden />
                  {t.preview.modeValue}
                </p>
                <p className="mt-1.5 text-[0.7rem] leading-snug text-cream-100/45">
                  {t.preview.modeNote}
                </p>
              </div>
            </aside>

            {/* Conversation */}
            <div className="flex min-h-[22rem] flex-col gap-5 p-5 md:p-7">
              <div className={cn("flex", dir === "rtl" ? "justify-start" : "justify-end")}>
                <p className="max-w-[85%] rounded-2xl bg-white/[0.06] px-4 py-3 text-sm text-cream-100/85">
                  {t.preview.conversation.user}
                </p>
              </div>

              <div className="flex gap-3">
                <span
                  aria-hidden
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/25"
                >
                  <Sparkles size={13} className="text-emerald-300" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-cream-100/80">
                    {t.preview.conversation.assistant}
                  </p>

                  <ul className="mt-4 space-y-2.5">
                    {steps.map((step, i) => {
                      const pending = i === steps.length - 1;
                      return (
                        <li
                          key={step}
                          className="rise-in flex items-center gap-2.5 text-[0.82rem]"
                          style={{ animationDelay: `${400 + i * 550}ms` }}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full",
                              pending
                                ? "border border-lavender-400/50"
                                : "bg-emerald-500/20 text-emerald-300",
                            )}
                          >
                            {pending ? (
                              <span className="h-1.5 w-1.5 rounded-full bg-lavender-300 pulse-soft" />
                            ) : (
                              <Check size={11} strokeWidth={3} />
                            )}
                          </span>
                          <span
                            className={
                              pending ? "text-lavender-200/90" : "text-cream-100/55"
                            }
                          >
                            {step}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Composer — decorative, completes the workspace illustration
                  so the conversation pane doesn't trail off into empty
                  space. Not an input: the mockup shows no live product. */}
              <div
                aria-hidden
                className="mt-auto flex items-center gap-3 rounded-xl border border-white/10 bg-ink-950/60 px-4 py-3"
              >
                <span className="flex-1 truncate text-sm text-cream-100/35">
                  {t.preview.composer}
                </span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/25">
                  <ArrowUp size={13} className="text-emerald-300" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
